<template>
  <div class="wallet-container">
    <div class="wallet-header">
      <div class="header-content">
        <button @click.stop="goBack" class="back-button">
          <span class="back-icon">←</span>
          <span>返回</span>
        </button>
        <div class="header-title">
          <div class="title-icon">💰</div>
          <h1>余额管理</h1>
        </div>
      </div>
    </div>

    <div class="wallet-content">
      <div class="table-card">
        <div class="table-header">
          <h2>余额列表（共 {{ total }} 条）</h2>
          <div class="action-buttons">
            <button class="add-button" @click="toggleForm">
              {{ showCreateForm ? '取消' : '新增' }}
            </button>
            <button class="metadata-button" type="button" @click="openWalletMetadataManagement">
              瞄定账户管理
            </button>
            <button class="refresh-button" :disabled="loading" @click="fetchWalletList">
              {{ loading ? '加载中...' : '刷新' }}
            </button>
            <button class="wechat-button" type="button" @click="openWechatReminder">
              微信提醒
            </button>
          </div>
        </div>
        <div class="list-filter-row">
          <div class="filter-input-group">
            <label for="opponent-target-search">锚定账户名称搜索</label>
            <input
              id="opponent-target-search"
              v-model.trim="selectedOpponentTargetName"
              list="opponent-target-options"
              type="text"
              placeholder="请选择或输入锚定账户名称"
            />
            <datalist id="opponent-target-options">
              <option v-for="name in opponentTargetOptions" :key="name" :value="name"></option>
            </datalist>
          </div>
          <button v-if="selectedOpponentTargetName" class="clear-filter-button" type="button" @click="clearOpponentTargetFilter">
            清空搜索
          </button>
        </div>

        <div v-if="errorMessage" class="status-message error">{{ errorMessage }}</div>
        <div v-if="successMessage" class="status-message success">{{ successMessage }}</div>

        <form v-if="showCreateForm" class="wallet-form" @submit.prevent="submitWalletForm">
          <div class="form-grid">
            <div class="input-group">
              <label for="username">店铺ID</label>
              <input id="username" v-model.trim="formModel.username" type="text" placeholder="请输入店铺ID" />
            </div>
            <div class="input-group">
              <label for="shop-name">店铺名称</label>
              <input id="shop-name" v-model.trim="formModel.shopName" type="text" placeholder="请输入店铺名称" />
            </div>
            <div class="input-group">
              <label for="organization-id">组织ID</label>
              <input id="organization-id" v-model.trim="formModel.organizationId" type="text" placeholder="请输入组织ID" />
            </div>
            <div class="input-group">
              <label for="opponent-target-id">锚定账户ID</label>
              <input id="opponent-target-id" v-model.trim="formModel.opponentTargetId" type="text" placeholder="请输入锚定账户ID" />
            </div>
            <div class="input-group">
              <label for="opponent-target-name">锚定账户名称</label>
              <input id="opponent-target-name" v-model.trim="formModel.opponentTargetName" type="text" placeholder="请输入锚定账户名称" />
            </div>
            
            <div class="input-group">
              <label for="target-id">目标账户ID</label>
              <input id="target-id" v-model.trim="formModel.targetId" type="text" placeholder="请输入目标账户ID" />
            </div>
            <div class="input-group">
              <label for="target-name">目标账户名称</label>
              <input id="target-name" v-model.trim="formModel.targetName" type="text" placeholder="请输入目标账户名称" />
            </div>
            <div class="input-group">
              <label for="transfer-amount">转账金额(元)</label>
              <input id="transfer-amount" v-model.trim="formModel.transferAmount" type="text" placeholder="请输入转账金额(元)" />
            </div>
          </div>
          <div class="form-actions">
            <button class="submit-button" type="submit" :disabled="submitting">
              {{ submitting ? '提交中...' : (editingId ? '提交修改' : '提交新增') }}
            </button>
            <button v-if="editingId" class="cancel-button" type="button" :disabled="submitting" @click="toggleForm">
              取消编辑
            </button>
          </div>
        </form>

        <div class="grouped-wallet-list">
          <div v-if="!loading && filteredGroupedWalletList.length === 0" class="desktop-empty">暂无数据</div>
          <div v-for="group in filteredGroupedWalletList" :key="group.key" class="group-wallet-card">
            <div class="group-anchor-card">
              <div class="group-label">锚定账户名称</div>
              <div class="group-anchor-name">{{ group.opponentTargetName }}</div>
              <div class="group-label">锚定账户余额</div>
              <div class="group-anchor-balance">{{ group.opponentTargetBalance }}</div>
            </div>
            <div class="group-targets">
              <div v-for="item in group.targets" :key="item.id" class="target-flow-row">
                <div class="flow-line"></div>
                <div class="group-target-card">
                  <div class="group-target-main">
                    <div class="group-label">目标账户名称</div>
                    <div class="desktop-target-name">{{ item.targetName ?? '-' }}</div>
                    <div class="group-label">目标余额</div>
                    <div class="desktop-target-balance" :class="getTargetBalanceClass(item.targetBalance)">
                      {{ item.targetBalance ?? '-' }}
                    </div>
                  </div>
                  <div class="row-action-buttons">
                    <button class="row-edit-button" type="button" :disabled="submitting || deletingId === item.id" @click="startEdit(item)">修改</button>
                    <button class="row-delete-button" type="button" :disabled="submitting || deletingId === item.id" @click="deleteWallet(item)">
                      {{ deletingId === item.id ? '删除中...' : '删除' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showWechatModal" class="wechat-modal-mask" @click.self="closeWechatReminder">
      <div class="wechat-modal">
        <div class="wechat-modal-header">
          <h3>微信提醒二维码</h3>
          <button class="wechat-close-button" type="button" @click="closeWechatReminder">关闭</button>
        </div>
        <div class="wechat-modal-body">
          <img class="wechat-qr-image" :src="wechatQrUrl" alt="微信提醒二维码" />
          <p class="wechat-link-text">{{ wechatNotifyLink }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'
import { createAdaptiveViewportMixin } from '@/mixins/adaptiveViewport'

export default {
  name: 'WalletManagement',
  mixins: [createAdaptiveViewportMixin({ designWidth: 1680, designHeight: 980, minScale: 0.5 })],
  data() {
    return {
      walletList: [],
      total: 0,
      loading: false,
      submitting: false,
      listRequesting: false,
      listRefreshTimer: null,
      cardNumber: '',
      showWechatModal: false,
      lowBalanceVoiceRecords: {},
      voiceReady: false,
      pendingVoiceTexts: [],
      voiceUnlockHandler: null,
      editingId: null,
      deletingId: null,
      showCreateForm: false,
      selectedOpponentTargetName: '',
      errorMessage: '',
      successMessage: '',
      formModel: {
        username: '',
        shopName: '',
        organizationId: '',
        opponentTargetId: '',
        opponentTargetName: '',
        targetId: '',
        targetName: '',
        transferAmount: ''
      }
    }
  },
  created() {
    this.cardNumber = this.$route.query.cardNumber || localStorage.getItem('cardNumber') || localStorage.getItem('username') || ''
    this.setupVoiceUnlock()
    this.fetchWalletList()
    this.startAutoRefresh()
  },
  beforeDestroy() {
    this.stopAutoRefresh()
    if (this.voiceUnlockHandler) {
      window.removeEventListener('click', this.voiceUnlockHandler)
      window.removeEventListener('keydown', this.voiceUnlockHandler)
      window.removeEventListener('touchstart', this.voiceUnlockHandler)
      this.voiceUnlockHandler = null
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel()
    }
  },
  computed: {
    wechatNotifyLink() {
      return `http://wxsq.xxdazz.com?card_number=${encodeURIComponent(this.cardNumber || '')}`
    },
    wechatQrUrl() {
      return `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(this.wechatNotifyLink)}`
    },
    groupedWalletList() {
      const groupMap = new Map()
      this.walletList.forEach(item => {
        const key = `${item.opponentTargetName || '-'}_${item.opponentTargetId || ''}`
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            key,
            opponentTargetName: item.opponentTargetName ?? '-',
            opponentTargetBalance: item.opponentTargetBalance ?? '-',
            targets: []
          })
        }
        const group = groupMap.get(key)
        group.targets.push(item)
      })
      return Array.from(groupMap.values())
    },
    opponentTargetOptions() {
      return this.groupedWalletList.map(group => group.opponentTargetName).filter(Boolean)
    },
    filteredGroupedWalletList() {
      const keyword = String(this.selectedOpponentTargetName || '').trim()
      if (!keyword) {
        return this.groupedWalletList
      }
      return this.groupedWalletList.filter(group => String(group.opponentTargetName || '').includes(keyword))
    }
  },
  methods: {
    applyAdaptiveViewportScale() {
      const el = this.$el
      if (!el) {
        return
      }
      const pageBackground = window.getComputedStyle(el).background || ''

      const isMobile = window.innerWidth <= 768
      if (isMobile) {
        document.documentElement.style.overflow = 'hidden'
        document.documentElement.style.width = '100%'
        document.documentElement.style.height = '100%'
        document.documentElement.style.background = pageBackground
        document.body.style.overflow = 'hidden'
        document.body.style.width = '100%'
        document.body.style.height = '100%'
        document.body.style.background = pageBackground

        el.style.zoom = '1'
        el.style.transformOrigin = 'top left'
        el.style.width = '100%'
        el.style.height = '100vh'
        el.style.minHeight = '100vh'
        el.style.overflowX = 'hidden'
        el.style.overflowY = 'auto'
        el.style.marginLeft = '0'
        el.style.marginRight = '0'
        return
      }

      const designWidth = 1680
      const designHeight = 980
      const minScale = 0.5
      const viewportWidth = Math.max(window.innerWidth || designWidth, 320)
      const viewportHeight = Math.max(window.innerHeight || designHeight, 560)
      const previousZoom = el.style.zoom
      const previousTransformOrigin = el.style.transformOrigin
      const previousWidth = el.style.width
      const previousHeight = el.style.height
      const previousMinHeight = el.style.minHeight
      const previousOverflow = el.style.overflow
      const previousMarginLeft = el.style.marginLeft
      const previousMarginRight = el.style.marginRight

      el.style.zoom = '1'
      el.style.transformOrigin = 'top left'
      el.style.width = `${designWidth}px`
      el.style.height = 'auto'
      el.style.minHeight = ''
      el.style.overflow = 'visible'
      el.style.marginLeft = '0'
      el.style.marginRight = '0'

      const contentHeight = Math.max(
        designHeight,
        el.scrollHeight || 0,
        el.offsetHeight || 0
      )

      el.style.zoom = previousZoom
      el.style.transformOrigin = previousTransformOrigin
      el.style.width = previousWidth
      el.style.height = previousHeight
      el.style.minHeight = previousMinHeight
      el.style.overflow = previousOverflow
      el.style.marginLeft = previousMarginLeft
      el.style.marginRight = previousMarginRight

      const widthScale = viewportWidth / designWidth
      const heightScale = viewportHeight / contentHeight
      const scale = Math.max(minScale, Math.min(widthScale, heightScale))
      const scaledWidth = designWidth * scale
      const horizontalMargin = Math.max((viewportWidth - scaledWidth) / 2 / scale, 0)

      document.documentElement.style.overflowX = 'hidden'
      document.documentElement.style.overflowY = 'auto'
      document.documentElement.style.width = '100%'
      document.documentElement.style.height = 'auto'
      document.documentElement.style.background = pageBackground
      document.body.style.overflowX = 'hidden'
      document.body.style.overflowY = 'hidden'
      document.body.style.width = '100%'
      document.body.style.height = 'auto'
      document.body.style.background = pageBackground

      el.style.zoom = String(scale)
      el.style.transformOrigin = 'top left'
      el.style.width = `${designWidth}px`
      el.style.height = 'auto'
      el.style.minHeight = `${contentHeight}px`
      el.style.overflowX = 'hidden'
      el.style.overflowY = 'visible'
      el.style.marginLeft = `${horizontalMargin}px`
      el.style.marginRight = `${horizontalMargin}px`
    },
    resetAdaptiveViewportScale() {
      const el = this.$el
      if (!el) {
        return
      }

      el.style.zoom = ''
      el.style.transformOrigin = ''
      el.style.width = ''
      el.style.height = ''
      el.style.minHeight = ''
      el.style.overflow = ''
      el.style.overflowX = ''
      el.style.overflowY = ''
      el.style.marginLeft = ''
      el.style.marginRight = ''

      const cache = this.__adaptiveViewportDocumentStyleCache || {}
      document.documentElement.style.overflow = cache.documentOverflow || ''
      document.documentElement.style.overflowX = ''
      document.documentElement.style.overflowY = ''
      document.documentElement.style.width = cache.documentWidth || ''
      document.documentElement.style.height = cache.documentHeight || ''
      document.documentElement.style.background = cache.documentBackground || ''
      document.body.style.overflow = cache.bodyOverflow || ''
      document.body.style.overflowX = ''
      document.body.style.overflowY = ''
      document.body.style.width = cache.bodyWidth || ''
      document.body.style.height = cache.bodyHeight || ''
      document.body.style.background = cache.bodyBackground || ''
    },
    toSafeHeaderValue(value) {
      const str = String(value ?? '')
      for (let i = 0; i < str.length; i += 1) {
        if (str.charCodeAt(i) > 255) {
          return encodeURIComponent(str)
        }
      }
      return str
    },
    goBack() {
      this.$router.push('/home')
    },
    openWalletMetadataManagement() {
      this.$router.push({
        path: '/wallet-metadata-management',
        query: this.cardNumber ? { cardNumber: this.cardNumber } : {}
      })
    },
    clearOpponentTargetFilter() {
      this.selectedOpponentTargetName = ''
    },
    openWechatReminder() {
      this.showWechatModal = true
    },
    closeWechatReminder() {
      this.showWechatModal = false
    },
    normalizeWalletList(list) {
      const normalizedList = (list || []).map(item => ({
        id: item.id,
        username: item.username,
        shopName: item.shopName ?? item.shop_name,
        organizationId: item.organizationId ?? item.organization_id,
        opponentTargetId: item.opponentTargetId ?? item.opponent_target_id,
        opponentTargetName: item.opponentTargetName ?? item.opponent_target_name,
        opponentTargetBalance: item.opponentTargetBalance ?? item.opponent_target_balance ?? item.opponentTargetBlance ?? item.opponent_target_blance,
        targetId: item.targetId ?? item.target_id,
        targetName: item.targetName ?? item.target_name,
        targetBalance: item.targetBalance ?? item.target_balance ?? item.targetBlance ?? item.target_blance,
        transferAmount: item.transferAmount ?? item.transfer_amount,
        createTime: item.createTime ?? item.create_time
      }))
      const lowBalanceItems = normalizedList.filter(item => this.getBalanceValue(item.targetBalance) < 200)
      const normalItems = normalizedList.filter(item => this.getBalanceValue(item.targetBalance) >= 200)
      return [...lowBalanceItems, ...normalItems]
    },
    getBalanceValue(value) {
      if (value === null || value === undefined || value === '') {
        return Number.MAX_SAFE_INTEGER
      }
      const parsed = Number(String(value).replace(/,/g, ''))
      return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER
    },
    getCardNumberHeaders() {
      const value = this.toSafeHeaderValue(this.cardNumber || localStorage.getItem('cardNumber') || localStorage.getItem('username') || '')
      return {
        cardnumber: value,
        cardNumber: value
      }
    },
    getTargetBalanceClass(balance) {
      return this.getBalanceValue(balance) < 300 ? 'target-balance-low' : 'target-balance-normal'
    },
    formatBalanceForVoice(balance) {
      if (balance === null || balance === undefined || balance === '') {
        return '未知'
      }
      return String(balance)
    },
    setupVoiceUnlock() {
      if (!window.speechSynthesis) {
        return
      }
      this.voiceUnlockHandler = () => {
        this.voiceReady = true
        window.speechSynthesis.resume()
        while (this.pendingVoiceTexts.length > 0) {
          const text = this.pendingVoiceTexts.shift()
          this.speakLowBalance(text, true)
        }
        if (this.voiceUnlockHandler) {
          window.removeEventListener('click', this.voiceUnlockHandler)
          window.removeEventListener('keydown', this.voiceUnlockHandler)
          window.removeEventListener('touchstart', this.voiceUnlockHandler)
          this.voiceUnlockHandler = null
        }
      }
      window.addEventListener('click', this.voiceUnlockHandler, { once: true })
      window.addEventListener('keydown', this.voiceUnlockHandler, { once: true })
      window.addEventListener('touchstart', this.voiceUnlockHandler, { once: true })
    },
    speakLowBalance(text, skipQueue = false) {
      if (!window.speechSynthesis) {
        return
      }
      if (!this.voiceReady && !skipQueue) {
        this.pendingVoiceTexts.push(text)
        return
      }
      window.speechSynthesis.resume()
      const utterance = new window.SpeechSynthesisUtterance(text)
      utterance.lang = 'zh-CN'
      utterance.rate = 1
      utterance.pitch = 1
      window.speechSynthesis.speak(utterance)
    },
    notifyLowTargetBalance(list) {
      const lowItems = (list || []).filter(item => this.getBalanceValue(item.targetBalance) < 300)
      if (lowItems.length === 0) {
        return
      }
      const now = Date.now()
      lowItems.forEach(item => {
        const key = item.id || `${item.shopName || ''}_${item.targetName || ''}`
        const lastSpeakAt = this.lowBalanceVoiceRecords[key] || 0
        if (now - lastSpeakAt < 25000) {
          return
        }
        this.lowBalanceVoiceRecords = {
          ...this.lowBalanceVoiceRecords,
          [key]: now
        }
        const text = `${item.targetName || '未知目标账户'}，目标余额过低，目前目标余额金额${this.formatBalanceForVoice(item.targetBalance)}`
        this.speakLowBalance(text)
      })
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.listRefreshTimer = setInterval(() => {
        this.fetchWalletList({ silent: true })
      }, 30000)
    },
    stopAutoRefresh() {
      if (this.listRefreshTimer) {
        clearInterval(this.listRefreshTimer)
        this.listRefreshTimer = null
      }
    },
    
    yuanToFen(yuanValue) {
      const amount = String(yuanValue || '').trim()
      if (!/^\d+(\.\d{1,2})?$/.test(amount)) {
        return null
      }
      const [integerPart, decimalPart = ''] = amount.split('.')
      const normalizedDecimal = `${decimalPart}00`.slice(0, 2)
      return Number(integerPart) * 100 + Number(normalizedDecimal)
    },
    toggleForm() {
      this.showCreateForm = !this.showCreateForm
      if (!this.showCreateForm) {
        this.editingId = null
        this.resetForm()
      }
      this.errorMessage = ''
      this.successMessage = ''
    },
    startEdit(item) {
      this.showCreateForm = true
      this.editingId = item.id
      this.formModel = {
        username: item.username || '',
        shopName: item.shopName || '',
        organizationId: item.organizationId !== null && item.organizationId !== undefined ? String(item.organizationId) : '',
        opponentTargetId: item.opponentTargetId !== null && item.opponentTargetId !== undefined ? String(item.opponentTargetId) : '',
        opponentTargetName: item.opponentTargetName || '',
        targetId: item.targetId !== null && item.targetId !== undefined ? String(item.targetId) : '',
        targetName: item.targetName || '',
        transferAmount: item.transferAmount
      }
      this.errorMessage = ''
      this.successMessage = ''
    },
    resetForm() {
      this.formModel = {
        username: '',
        shopName: '',
        organizationId: '',
        opponentTargetId: '',
        opponentTargetName: '',
        targetId: '',
        targetName: '',
        transferAmount: ''
      }
    },
    async submitWalletForm() {
      const requiredFields = [
        'username',
        'shopName',
        'organizationId',
        'opponentTargetId',
        'opponentTargetName',
        'targetId',
        'targetName',
        'transferAmount'
      ]
      const hasEmptyField = requiredFields.some(key => !String(this.formModel[key]).trim())
      if (hasEmptyField) {
        this.errorMessage = '请完整填写新增表单'
        this.successMessage = ''
        return
      }

      const numericPattern = /^\d+$/
      if (![this.formModel.organizationId, this.formModel.opponentTargetId, this.formModel.targetId].every(value => numericPattern.test(value))) {
        this.errorMessage = '组织ID、锚定账户ID、目标账户ID必须为数字'
        this.successMessage = ''
        return
      }
      const transferAmountFen = this.yuanToFen(this.formModel.transferAmount)
      if (transferAmountFen === null) {
        this.errorMessage = '转账金额格式不正确，请输入最多两位小数的元金额'
        this.successMessage = ''
        return
      }

      this.submitting = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const token = localStorage.getItem('authToken')
        const safeToken = this.toSafeHeaderValue(token)
        const payload = {
          cardNumber: this.cardNumber,
          username: this.formModel.username,
          shopName: this.formModel.shopName,
          organizationId: Number(this.formModel.organizationId),
          opponentTargetId: Number(this.formModel.opponentTargetId),
          opponentTargetName: this.formModel.opponentTargetName,
          targetId: Number(this.formModel.targetId),
          targetName: this.formModel.targetName,
          transferAmount: transferAmountFen
        }
        if (this.editingId) {
          payload.id = Number(this.editingId)
        }
        const requestConfig = {
          headers: {
            ...(token ? { Authorization: `Bearer ${safeToken}` } : {}),
            'Content-Type': 'application/json',
            ...this.getCardNumberHeaders()
          }
        }
        const response = this.editingId
          ? await axios.put(API_CONFIG.wallet(), payload, requestConfig)
          : await axios.post(API_CONFIG.wallet(), payload, requestConfig)
        if (response.data && response.data.code === 200) {
          this.successMessage = response.data.msg || (this.editingId ? '修改成功' : '新增成功')
          this.showCreateForm = false
          this.editingId = null
          this.resetForm()
          await this.fetchWalletList()
        } else {
          this.errorMessage = (response.data && (response.data.msg || response.data.message)) || (this.editingId ? '修改失败' : '新增失败')
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.msg || error.response?.data?.message || (this.editingId ? '修改失败' : '新增失败')
      } finally {
        this.submitting = false
      }
    },
    async deleteWallet(item) {
      if (!item || !item.id) {
        return
      }
      const confirmed = window.confirm('确认删除该条数据吗？')
      if (!confirmed) {
        return
      }
      this.deletingId = item.id
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const token = localStorage.getItem('authToken')
        const safeToken = this.toSafeHeaderValue(token)
        const response = await axios.delete(`${API_CONFIG.wallet()}/${item.id}`, {
          params: this.cardNumber ? { cardNumber: this.cardNumber } : {},
          headers: {
            ...(token
            ? {
                Authorization: `Bearer ${safeToken}`
              }
            : {}),
            ...this.getCardNumberHeaders()
          }
        })
        if (response.data && response.data.code === 200) {
          this.successMessage = response.data.msg || '删除成功'
          if (this.editingId === item.id) {
            this.showCreateForm = false
            this.editingId = null
            this.resetForm()
          }
          await this.fetchWalletList()
        } else {
          this.errorMessage = (response.data && (response.data.msg || response.data.message)) || '删除失败'
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.msg || error.response?.data?.message || '删除失败'
      } finally {
        this.deletingId = null
      }
    },
    async fetchWalletList(options = {}) {
      const { silent = false } = options
      if (this.listRequesting) {
        return
      }
      this.listRequesting = true
      if (!silent) {
        this.loading = true
        this.errorMessage = ''
      }
      try {
        const token = localStorage.getItem('authToken')
        const safeToken = this.toSafeHeaderValue(token)
        const response = await axios.get(API_CONFIG.walletList(), {
          params: this.cardNumber ? { cardNumber: this.cardNumber } : {},
          headers: {
            ...(token
            ? {
                Authorization: `Bearer ${safeToken}`
              }
            : {}),
            ...this.getCardNumberHeaders()
          }
        })
        if (response.data && response.data.code === 200) {
          const rows = response.data.rows || response.data.data || []
          this.walletList = this.normalizeWalletList(rows)
          this.total = Number(response.data.total || this.walletList.length || 0)
          this.notifyLowTargetBalance(this.walletList)
        } else {
          if (!silent) {
            this.errorMessage = (response.data && (response.data.msg || response.data.message)) || '获取余额列表失败'
            this.walletList = []
            this.total = 0
          }
        }
      } catch (error) {
        if (!silent) {
          this.errorMessage = error.response?.data?.msg || error.response?.data?.message || '获取余额列表失败'
          this.walletList = []
          this.total = 0
        }
      } finally {
        this.listRequesting = false
        if (!silent) {
          this.loading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.wallet-container {
  min-height: 100vh;
  height: 100vh;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.wallet-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 14px 0;
}

.header-content {
  width: min(calc(100% - 32px), 1200px);
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  padding: 10px 20px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-5px);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
}

.header-title h1 {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.wallet-content {
  flex: 1;
  width: min(calc(100% - 32px), 1700px);
  margin: 0 auto;
  padding: 16px 0 18px;
  overflow: hidden;
  min-height: 0;
  box-sizing: border-box;
}

.table-card {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 18px;
}

.grouped-wallet-list {
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.table-header h2 {
  margin: 0;
  color: white;
  font-size: 20px;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}

.list-filter-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.filter-input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 320px;
}

.filter-input-group label {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
}

.filter-input-group input {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  padding: 10px 12px;
  outline: none;
}

.filter-input-group input::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.clear-filter-button {
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  padding: 8px 14px;
  cursor: pointer;
}

.add-button,
.metadata-button,
.refresh-button,
.wechat-button {
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  padding: 8px 16px;
  cursor: pointer;
}

.refresh-button:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.wechat-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(5, 16, 40, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.wechat-modal {
  width: min(420px, 100%);
  border-radius: 14px;
  background: #ffffff;
  color: #1f2a44;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.35);
}

.wechat-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #e7edf6;
}

.wechat-modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.wechat-close-button {
  border: none;
  border-radius: 8px;
  background: #eef2fb;
  color: #36527d;
  padding: 7px 12px;
  cursor: pointer;
}

.wechat-modal-body {
  padding: 16px 14px 18px;
  text-align: center;
}

.wechat-qr-image {
  width: 260px;
  height: 260px;
  max-width: 100%;
  border-radius: 10px;
  border: 1px solid #dde6f4;
}

.wechat-link-text {
  margin: 10px 0 0;
  color: #586b87;
  font-size: 12px;
  word-break: break-all;
}

.wallet-form {
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  padding: 14px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 12px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-group label {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
}

.input-group input {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  padding: 10px 12px;
  outline: none;
}

.input-group input::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.input-group input:focus {
  border-color: #00d4ff;
}

.form-actions {
  margin-top: 12px;
}

.submit-button {
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 999px;
  background: linear-gradient(45deg, #26c6da, #00acc1);
  color: white;
  padding: 8px 18px;
  cursor: pointer;
}

.submit-button:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.cancel-button {
  margin-left: 10px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 8px 18px;
  cursor: pointer;
}

.cancel-button:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.desktop-empty {
  border: 1px dashed rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  text-align: center;
  color: rgba(255, 255, 255, 0.82);
  padding: 22px 16px;
}

.grouped-wallet-list {
  margin-top: 12px;
}

.group-wallet-card {
  display: flex;
  align-items: stretch;
  gap: 12px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.08);
  padding: 14px;
}

.group-wallet-card + .group-wallet-card {
  margin-top: 12px;
}

.group-anchor-card {
  width: 250px;
  flex-shrink: 0;
  border-radius: 12px;
  background: rgba(53, 90, 150, 0.38);
  border: 1px solid rgba(126, 217, 255, 0.32);
  padding: 14px 12px;
}

.group-label {
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  line-height: 1.2;
}

.group-anchor-name {
  margin-top: 6px;
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  word-break: break-all;
}

.group-anchor-balance {
  margin-top: 6px;
  color: #7ed9ff;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.15;
}

.group-targets {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.target-flow-row {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.flow-line {
  width: 66px;
  border-radius: 8px;
  border: 1px solid rgba(126, 217, 255, 0.35);
  background: linear-gradient(90deg, rgba(0, 212, 255, 0.05), rgba(0, 212, 255, 0.35), rgba(0, 212, 255, 0.05));
  background-size: 200% 100%;
  animation: flow-move 1.6s linear infinite;
}

.group-target-card {
  flex: 1;
  min-width: 0;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.group-target-main {
  min-width: 0;
}

.desktop-target-name {
  margin-top: 6px;
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
  word-break: break-all;
}

.desktop-target-balance {
  margin-top: 6px;
  font-size: 28px;
  line-height: 1.2;
}

.target-balance-normal {
  color: #ffd166;
  font-weight: 800;
}

.target-balance-low {
  color: #ff6b6b;
  font-weight: 800;
}

.row-action-buttons {
  display: flex;
  gap: 8px;
}

.row-edit-button,
.row-delete-button {
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 12px;
}

.row-delete-button {
  border-color: rgba(255, 107, 107, 0.55);
  color: #ffc1c1;
}

.row-edit-button:disabled,
.row-delete-button:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

@keyframes flow-move {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: 0 0;
  }
}

.status-message.error {
  margin-bottom: 16px;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.4);
  color: #ff9f9f;
}

.status-message.success {
  margin-bottom: 16px;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  background: rgba(46, 213, 115, 0.2);
  border: 1px solid rgba(46, 213, 115, 0.4);
  color: #7dffb8;
}

@media (max-width: 992px) {
  .wallet-content {
    padding: 18px 12px 24px;
  }

  .table-card {
    padding: 14px;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .table-header h2 {
    font-size: 18px;
  }

  .action-buttons {
    width: 100%;
    flex-wrap: wrap;
  }

  .list-filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-input-group {
    min-width: 0;
  }

  .add-button,
  .metadata-button,
  .refresh-button,
  .wechat-button {
    flex: 1;
    min-width: 110px;
    text-align: center;
    padding: 9px 12px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .cancel-button {
    margin-left: 0;
  }
}

@media (max-width: 768px) {
  .wallet-container {
    min-height: 100vh;
    height: auto;
    overflow-y: auto;
  }

  .wallet-content {
    padding: 10px 8px 16px;
    overflow: visible;
  }

  .table-card {
    border-radius: 12px;
    padding: 10px;
  }

  .wallet-header {
    padding: 10px 0;
  }

  .header-content {
    padding: 0 10px;
    gap: 10px;
  }

  .back-button {
    padding: 7px 12px;
    font-size: 12px;
    border-radius: 18px;
  }

  .title-icon {
    font-size: 18px;
  }

  .header-title h1 {
    font-size: 18px;
  }

  .table-header {
    gap: 6px;
    margin-bottom: 8px;
  }

  .table-header h2 {
    font-size: 16px;
  }

  .action-buttons {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .add-button,
  .metadata-button,
  .refresh-button,
  .wechat-button {
    width: 100%;
    min-width: 0;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 12px;
  }

  .list-filter-row {
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.08);
    padding: 8px;
    margin-bottom: 8px;
  }

  .filter-input-group input {
    border-radius: 8px;
    padding: 9px 10px;
    font-size: 13px;
  }

  .clear-filter-button {
    width: 100%;
    border-radius: 8px;
    text-align: center;
    padding: 8px 10px;
    font-size: 12px;
  }

  .wallet-form {
    border-radius: 10px;
    padding: 10px;
    margin-bottom: 10px;
  }

  .submit-button,
  .cancel-button {
    width: 100%;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 12px;
    margin-left: 0;
  }

  .group-wallet-card {
    flex-direction: column;
    border-radius: 10px;
    padding: 8px;
    gap: 6px;
  }

  .group-anchor-card {
    width: 100%;
    border-radius: 8px;
    padding: 8px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 6px;
    align-items: end;
  }

  .group-anchor-name {
    font-size: 15px;
    margin-top: 0;
  }

  .group-anchor-balance {
    font-size: 20px;
    margin-top: 0;
  }

  .target-flow-row {
    flex-direction: column;
    gap: 6px;
  }

  .flow-line {
    width: 100%;
    height: 10px;
    border-radius: 999px;
  }

  .group-target-card {
    border-radius: 8px;
    padding: 8px;
    flex-direction: column;
    align-items: flex-start;
  }

  .group-target-main {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px 6px;
    align-items: end;
  }

  .desktop-target-name {
    font-size: 16px;
    margin-top: 0;
  }

  .desktop-target-balance {
    font-size: 22px;
    margin-top: 0;
  }

  .row-action-buttons {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .row-edit-button,
  .row-delete-button {
    width: 100%;
    border-radius: 8px;
    padding: 7px 10px;
    font-size: 12px;
  }
}

@media (max-width: 576px) {
  .header-content {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px;
  }

  .back-button {
    padding: 6px 10px;
    font-size: 11px;
  }

  .header-title {
    width: 100%;
    gap: 8px;
  }

  .title-icon {
    font-size: 18px;
  }

  .header-title h1 {
    font-size: 16px;
  }

  .table-card {
    padding: 8px;
  }

  .table-header h2 {
    font-size: 15px;
    line-height: 1.4;
  }

  .wechat-modal-mask {
    padding: 12px;
  }

  .wechat-modal {
    border-radius: 10px;
  }

  .wechat-modal-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .wechat-close-button {
    width: 100%;
  }

  .wechat-modal-body {
    padding: 12px 10px 14px;
  }

  .wechat-qr-image {
    width: 200px;
    height: 200px;
  }

  .wallet-form {
    padding: 8px;
  }

  .group-wallet-card {
    padding: 8px;
  }

  .group-anchor-card {
    grid-template-columns: 1fr;
  }

  .group-anchor-name {
    font-size: 14px;
  }

  .group-anchor-balance {
    font-size: 18px;
  }

  .group-target-main {
    grid-template-columns: 1fr;
  }

  .desktop-target-name {
    font-size: 15px;
  }

  .desktop-target-balance {
    font-size: 20px;
  }

  .row-action-buttons {
    grid-template-columns: 1fr;
  }
}
</style>
